import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import LazyImage from "../components/LazyImage";

const GalleryPage = () => {
  const galleryImages = [
    {
      id: 1,
      title: "Traditional Septic Installation",
      description: "Complete traditional septic system with distribution box",
      image: "/seo-images/4z8mh8az_unnamed.webp",
      category: "Traditional"
    },
    {
      id: 2,
      title: "Septic System Excavation",
      description: "Professional excavation for new septic installation",
      image: "/seo-images/f69s3hj7_sep.webp",
      category: "Traditional"
    },
    {
      id: 3,
      title: "Distribution Field Preparation",
      description: "Preparing the drain field for system installation",
      image: "/seo-images/jgz2o8et_sep1.webp",
      category: "Traditional"
    },
    {
      id: 4,
      title: "Eljen Mound System Installation",
      description: "Advanced Eljen mound system being installed",
      image: "/seo-images/dmesnkeu_Eljen1.webp",
      category: "Eljen"
    },
    {
      id: 5,
      title: "Completed Septic Field",
      description: "Finished septic field ready for backfilling",
      image: "/seo-images/f69s3hj7_sep.webp",
      category: "Traditional"
    },
    {
      id: 6,
      title: "Tank Installation Process",
      description: "Septic tank being positioned and leveled",
      image: "/seo-images/4z8mh8az_unnamed.webp",
      category: "Traditional"
    }
  ];

  return (
    <div>
      <SEO
        title="Project Gallery | Groundhogs™ Septic Systems (Thunder Bay)"
        description="View completed septic system installations and projects across Thunder Bay and Northwestern Ontario."
        canonicalPath="/gallery"
      />
      <Header />

      {/* Hero Section */}
      <section className="gh-intro pt-32 pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="gh-intro-heading text-center mb-12" data-reveal>
            <h1 className="text-5xl font-bold mb-6" style={{ color: "var(--dark-navy)" }}>
              Septic Installation Project Gallery
            </h1>
            <p className="text-xl max-w-3xl mx-auto" style={{ color: "var(--text-light)" }}>
              See the quality and craftsmanship of our septic installations across Thunder Bay and Northwestern Ontario. 
              From traditional systems to advanced Eljen mound installations.
            </p>
          </div>

          {/* Gallery Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {galleryImages.map((item) => (
              <div key={item.id} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl" style={{ transition: "box-shadow 0.3s ease" }}>
                <div className="relative h-64 overflow-hidden">
                  <LazyImage
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover hover:scale-110"
                    style={{ transition: "transform 0.3s ease" }}
                  />
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 rounded-full text-sm font-semibold text-white" style={{ backgroundColor: item.category === "Eljen" ? "var(--accent-green)" : "var(--primary-orange)" }}>
                      {item.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2" style={{ color: "var(--dark-navy)" }}>
                    {item.title}
                  </h3>
                  <p style={{ color: "var(--text-light)" }}>
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Video Project Showcases */}
          <div className="mt-16">
            <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: "var(--dark-navy)" }}>
              Featured Project Videos
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Video 1 */}
              <div className="bg-white rounded-lg overflow-hidden shadow-lg">
                <div className="relative h-48 bg-gray-200">
                  <img 
                    src="/seo-images/f69s3hj7_sep.webp"
                    alt="Septic system site preparation and excavation in Thunder Bay"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2" style={{ color: "var(--dark-navy)" }}>
                    Septic System Site Preparation
                  </h3>
                  <p className="text-sm" style={{ color: "var(--text-light)" }}>
                    Watch our professional crew prepare a challenging site for septic installation in Thunder Bay.
                  </p>
                </div>
              </div>

              {/* Video 2 */}
              <div className="bg-white rounded-lg overflow-hidden shadow-lg">
                <div className="relative h-48 bg-gray-200">
                  <img 
                    src="/seo-images/4z8mh8az_unnamed.webp"
                    alt="Professional septic tank installation by Groundhogs™ Environmental crew"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2" style={{ color: "var(--dark-navy)" }}>
                    Complete Tank Installation
                  </h3>
                  <p className="text-sm" style={{ color: "var(--text-light)" }}>
                    From excavation to backfill - see a full traditional septic tank installation time-lapse.
                  </p>
                </div>
              </div>

              {/* Video 3 */}
              <div className="bg-white rounded-lg overflow-hidden shadow-lg">
                <div className="relative h-48 bg-gray-200">
                  <img 
                    src="/seo-images/dmesnkeu_Eljen1.webp"
                    alt="Completed Eljen mound septic system installation in Northwestern Ontario"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2" style={{ color: "var(--dark-navy)" }}>
                    Eljen Mound System Build
                  </h3>
                  <p className="text-sm" style={{ color: "var(--text-light)" }}>
                    Advanced Eljen mound installation showing our systemized 3-day average process.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-16 text-center p-8 rounded-lg" style={{ backgroundColor: "var(--light-gray)" }}>
            <h2 className="text-3xl font-bold mb-4" style={{ color: "var(--dark-navy)" }}>
              Ready for Your Professional Septic Installation?
            </h2>
            <p className="text-lg mb-6" style={{ color: "var(--text-dark)" }}>
              Get your fast virtual estimate today. Systemized 3-day average installations with 100% permit approval rate.
            </p>
            <a
              href="/contact"
              className="inline-block px-8 py-4 rounded-md font-bold text-white text-lg hover:opacity-90"
              style={{ backgroundColor: "var(--primary-orange)" }}
            >
              Get Free Estimate
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GalleryPage;